import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';
import { Button, CreatePostSplitButton } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { ICONS } from '@/constants/icons';
// import { PostStats } from '@/components/creator/PostStats'; // Hidden for now
import { PostList } from '@/components/creator/PostList';
import { PostFilterDrawer } from '@/components/creator/PostFilterDrawer';
import { PostCreator } from '@/components/PostCreator';
import { FiltersIndicator } from '@/components/layout/FiltersIndicator';
import { BulkImportDialog } from '@/components/bulk/BulkImportDialog';
import { CanvaImportDialog } from '@/components/canva';
import { useAuth } from '@/hooks/useAuth';
import { usePostsStore, setPostsListActive } from '@/stores/postsStore';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { useCanvaImportStore } from '@/stores/canvaImportStore';
import { useSubmitForApproval } from '@/hooks/useApproval';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';

/**
 * Creator Page (Agile Creator)
 *
 * Full-featured post management dashboard with:
 * - Brand header with gradient
 * - Real-time statistics
 * - Search and filter functionality
 * - Post table view
 * - Bulk actions
 * - Quick post creation modal
 */

export const Creator = () => {
  const { t } = useTranslation(['posts', 'common']);
  const invalidateRelease = useInvalidateRelease();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [canvaImportOpen, setCanvaImportOpen] = useState(false);
  const [showPostCreator, setShowPostCreator] = useState(false);
  const [openWithSchedule, setOpenWithSchedule] = useState(false);
  const { fetchPosts, fetchStats, filters, setFilter } = usePostsStore();

  // Apply URL params as filters (e.g., ?label=Canva from email links)
  useEffect(() => {
    const labelParam = searchParams.get('label');
    if (labelParam && labelParam !== filters.tags) {
      setFilter('tags', labelParam);
      // Clear the URL param after applying to avoid re-triggering
      searchParams.delete('label');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, filters.tags, setFilter]);

  // Apply publishingStatus URL param (e.g., ?publishingStatus=failed from dashboard links)
  useEffect(() => {
    const publishingStatusParam = searchParams.get('publishingStatus');
    if (publishingStatusParam && publishingStatusParam !== filters.publishingStatus) {
      setFilter('publishingStatus', publishingStatusParam);
      searchParams.delete('publishingStatus');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, filters.publishingStatus, setFilter]);

  // Apply workflowStatus URL param (e.g., ?workflowStatus=PENDING from Approval Board "+N more" links)
  useEffect(() => {
    const workflowStatusParam = searchParams.get('workflowStatus');
    if (workflowStatusParam && workflowStatusParam !== filters.workflowStatus) {
      setFilter('workflowStatus', workflowStatusParam);
      searchParams.delete('workflowStatus');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, filters.workflowStatus, setFilter]);

  // Open PostCreator in edit mode when navigated here with ?editPostId=<id>
  // (e.g. from Approval Board "Edit Post" button)
  useEffect(() => {
    const editPostIdParam = searchParams.get('editPostId');
    if (editPostIdParam) {
      const parsedId = parseInt(editPostIdParam, 10);
      if (!isNaN(parsedId)) {
        setEditingPostId(parsedId);
        setOpenWithSchedule(false);
        setShowPostCreator(true);
      }
      // Clear the URL param so back-navigation doesn't re-open the modal
      searchParams.delete('editPostId');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only — URL param is the trigger

  // Open PostCreator in fresh-create mode when navigated here with ?new=1
  // (e.g. from PWA "Create Post" shortcut)
  useEffect(() => {
    const newParam = searchParams.get('new');
    if (newParam === '1') {
      setEditingPostId(null);
      setOpenWithSchedule(false);
      setShowPostCreator(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only — URL param is the trigger

  const { activeImport } = useCanvaImportStore();

  // Approval workflow hooks
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const submitForApprovalMutation = useSubmitForApproval();

  // Workflow column visible to everyone (including clients) when approval is active
  const workflowEnabled = isAgencyTeam && !carteBlanche;

  /**
   * Handle sending a post for review (submit for approval)
   */
  const handleSendForReview = async (postId: number) => {
    try {
      await submitForApprovalMutation.mutateAsync({ postId });
      announceReleaseMoment('sent');
    } catch (error) {
      const message = formatApiError(error).message;
      toast.error(message);
    }
  };

  // Fetch initial data (only on mount - empty dependencies to prevent infinite loop)
  useEffect(() => {
    fetchPosts();
    fetchStats();
    // Register with the cache-sync bridge so invalidateReleaseState() refreshes this list
    setPostsListActive(true);
    return () => setPostsListActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: only fetch once on mount, NOT on function changes

  // Team switch / God Mode enter-exit while this page stays mounted: AuthContext resets
  // postsStore (rows, stats, filters) without fetching, so refetch for the new context.
  // Skips the initial mount (handled above). Reset runs synchronously after setUser, so it
  // precedes this effect.
  const { user } = useAuth();
  const effectiveTeamId = user?.currentTeamId ?? user?.teamId ?? null;
  const lastTeamIdRef = useRef(effectiveTeamId);
  useEffect(() => {
    if (lastTeamIdRef.current === effectiveTeamId) return;
    lastTeamIdRef.current = effectiveTeamId;
    if (effectiveTeamId == null) return; // logged out: nothing to fetch
    void usePostsStore.getState().fetchPosts();
    void usePostsStore.getState().fetchStats();
  }, [effectiveTeamId]);

  // Safety net: the list lives in Zustand (no React Query focus refetch). When the tab becomes
  // visible again, silently refresh (keeps all loaded "Load more" pages), throttled to 15s.
  useEffect(() => {
    let lastSync = Date.now();
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSync < 15_000) return;
      lastSync = Date.now();
      void usePostsStore.getState().refreshIfActive();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // Watch for reopen request from background import - just open the dialog
  // The dialog will clear the activeImport after reading the jobId
  useEffect(() => {
    if (activeImport?.requestReopen) {
      setCanvaImportOpen(true);
    }
  }, [activeImport?.requestReopen]);

  const handleCreatePost = () => {
    setEditingPostId(null);
    setShowPostCreator(true);
  };

  const handleEditPost = (postId: number) => {
    setEditingPostId(postId);
    setOpenWithSchedule(false);
    setShowPostCreator(true);
  };

  const handleScheduleNow = (postId: number) => {
    setEditingPostId(postId);
    setOpenWithSchedule(true);
    setShowPostCreator(true);
  };

  const handleClosePostCreator = () => {
    setShowPostCreator(false);
    setEditingPostId(null);
    setOpenWithSchedule(false);
  };

  // Detect when toolbar becomes stuck (scrolled past its natural position)
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [, setIsToolbarStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsToolbarStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Prepare highlighted title (similar to BrandHeader approach)
  const creatingWord = t('common:creating');
  const fullTitle = t('common:what_creating_today', { creating: creatingWord });
  const titleParts = fullTitle.split(creatingWord);

  return (
    <div>
        {/* Page Header */}
        <PageHeader
          title={
            <>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span className="text-bs-secondary">Ä</span>gile Creator
            </>
          }
          subtitle={
            <>
              {titleParts[0]}
              <span className="text-bs-secondary">{creatingWord}</span>
              {titleParts[1]}
            </>
          }
          icon={ICONS.MAGIC_WAND}
          headerType="list"
          documentTitle="Ägile Creator"
        />

        {/* Main content with spacing */}
        <div className="space-y-4">
          {/* Sentinel to detect when toolbar becomes sticky */}
          <div ref={sentinelRef} className="h-0" aria-hidden="true" />
          {/* Toolbar */}
          <div className="flex items-center gap-1.5 sm:gap-2 py-2 sm:py-3 sticky top-navbar-height z-30 -mx-6 px-6 bg-gray-50">
            {/* Filter Button */}
            <Button
              variant="outline"
              onClick={() => setFilterDrawerOpen(true)}
              className="flex items-center gap-2 !h-9 sm:!h-10"
            >
              <Icon name={ICONS.FILTER} size="sm" ariaHidden />
              <span className="hidden sm:inline">{t('common:filters')}</span>
            </Button>

            {/* Search */}
            <div className="flex-1 min-w-0 lg:flex-none lg:w-64">
              <Input
                type="search"
                placeholder={t('common:search_posts')}
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                className="w-full !h-9 sm:!h-10 !py-0"
              />
            </div>

            {/* Spacer — desktop only */}
            <div className="hidden lg:block flex-1" />

            {/* Create Post Split Button */}
            <CreatePostSplitButton
              onCreatePost={handleCreatePost}
              onBulkImport={() => setBulkImportOpen(true)}
              onCanvaImport={() => setCanvaImportOpen(true)}
            />
          </div>

          {/* Active Filters Indicator */}
          <FiltersIndicator />

          {/* Post List (Full Width) */}
          <PostList
            onEdit={handleEditPost}
            onSendForReview={handleSendForReview}
            onScheduleNow={handleScheduleNow}
            workflowEnabled={workflowEnabled}
          />
        </div>

        {/* Post Filter Drawer */}
        <PostFilterDrawer
          isOpen={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
        />

        {/* Post Creator Modal */}
        <PostCreator
          isOpen={showPostCreator}
          onClose={handleClosePostCreator}
          editingPostId={editingPostId || undefined}
          initialScheduleOpen={openWithSchedule}
          initialScheduleMode="shoot_now"
          onSaveSuccess={() => {
            // Cross-page cache sync (calendar, board, variations, dashboard, portfolio + this list)
            invalidateRelease();
            handleClosePostCreator();
          }}
        />

        {/* Bulk Import Dialog */}
        <BulkImportDialog
          isOpen={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          onSuccess={() => {
            // Refresh posts list and stats after successful import
            fetchPosts();
            fetchStats();
          }}
        />

        {/* Canva Import Dialog */}
        <CanvaImportDialog
          isOpen={canvaImportOpen}
          onClose={() => setCanvaImportOpen(false)}
          onSuccess={() => {
            // Media imported to library - no need to refresh posts
            setCanvaImportOpen(false);
          }}
        />

      </div>
  );
};
